import { MS_IN_DAY } from "./usageConstants";

/**
 * Convert a timestamp in milliseconds to an HTML `<input type="date">` value
 * in the user's local timezone.
 */
export function toLocalDateInputValue(timestampMs: number): string {
  const date = new Date(timestampMs);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function toLocalDateTimeInputValue(timestampMs: number): string {
  const date = new Date(timestampMs);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

/**
 * Parse an HTML `<input type="date">` value into year, monthIndex (0-based),
 * and day components, or `null` when the value is invalid.
 */
export function parseLocalDateInputValue(
  value: string,
): { year: number; monthIndex: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  ) {
    return null;
  }
  if (month < 1 || month > 12) {
    return null;
  }
  if (day < 1 || day > 31) {
    return null;
  }
  return { day, monthIndex: month - 1, year };
}

export function parseLocalDateTimeInputValue(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return new Date(year, month - 1, day, hour, minute, 0, 0).getTime();
}

/**
 * Return the epoch-ms timestamp for the start of a local day described by an
 * HTML date input value, or `null` when the value cannot be parsed.
 */
export function startOfLocalDayMs(value: string): number | null {
  const parsed = parseLocalDateInputValue(value);
  if (!parsed) {
    return null;
  }
  return new Date(
    parsed.year,
    parsed.monthIndex,
    parsed.day,
    0,
    0,
    0,
    0,
  ).getTime();
}

/**
 * Return the epoch-ms timestamp for the end of a local day described by an
 * HTML date input value, or `null` when the value cannot be parsed.
 */
export function endOfLocalDayMs(value: string): number | null {
  const start = startOfLocalDayMs(value);
  if (start === null) {
    return null;
  }
  return start + MS_IN_DAY - 1;
}

/**
 * Trim a text input value and return `undefined` when empty so it can be used
 * directly as an optional filter field.
 */
export function normalizeTextFilter(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

/**
 * Split a comma-separated input string into an array, trimming each entry.
 * Returns `undefined` when no non-empty entries remain.
 */
export function normalizeListFilter(value: string): string[] | undefined {
  const list = value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
  return list.length > 0 ? list : undefined;
}

/**
 * Parse a numeric input value, returning `undefined` when empty or invalid.
 */
export function normalizeNumberFilter(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}
